import React, { useContext, useEffect, useMemo, useState } from "react";
import { OrgContext } from "contexts/OrgContext";
import { BillingContext } from "contexts/BillingContext";
import { UserContext } from "contexts/UserContext";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { useHistory } from "react-router";
import {
  GPUInstanceType,
  GetProviderName,
  Location,
} from "types/InstanceTypes";
import { displayPlatformType } from "../utils/index";
import Button from "components/UI-lib/Button";
import { classNames, regionToReadable } from "components/utils";
import InlineNotification from "contexts/Notifications/InlineNotifications";
import { CubeIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { Tooltip } from "@mui/material";
import { Button as ButtonPrimitives } from "components/Graphs/Button";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "components/Graphs/Drawer";
import { InfoTooltip } from "components/Graphs/InfoTooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/Graphs/Select";
import { Input } from "components/Graphs/Input";
import { Checkbox } from "components/Graphs/Checkbox";
import DrawerContainerSelector from "./DrawerContainerSelector/index";
import { ContainerSelected } from "../shared/BuildTypes";
import { allPublicCloudsName, hasVerbBaseImageContainer } from "./utils/index";
import { buildDefaultVerbYamlConfig } from "components/Verb/utils";
import posthog from "posthog-js";
import { toast } from "utils/toast";
import {
  useFreeTier,
  getStoragePricing,
  isFreeTierInstance,
  getBasePriceAmount,
} from "utils/freeTierUtils";
import { getInstanceCloudCreds, isLaunchpadInstance } from "utils/cloudCreds";
import { shouldShowCreateInstanceBillingModal } from "./billingGate";
import { isOrgAdminRole } from "utils/roleUtils";

const displayContainerName = (containerName: string) => {
  if (containerName.length > 20) {
    return containerName.slice(0, 20) + "...";
  }
  return containerName;
};

const getBuildModeLabel = (container: ContainerSelected): string => {
  if (container?.vmBuild?.k8s && !container?.vmBuild?.k8s?.isDisabled) {
    return "Kubernetes";
  }
  if (container?.vmBuild) {
    return "VM Mode";
  }
  if (container?.dockerCompose) {
    return "Docker Compose";
  }
  if (container?.verbBuild) {
    return "Featured Container";
  }
  if (container?.customContainer) {
    return "Custom Container";
  }
  return "Unknown";
};

import { LaunchableConfig } from "server";
import { NameEnvironment } from "./NameEnvironment";

interface DeploymentSectionProps {
  selectedInstanceType: GPUInstanceType;
  container: ContainerSelected;
  setContainer: (container: ContainerSelected) => void;
  environmentName: string;
  handleNameChange: (val: string) => void;
  nameFieldErrorMessage: string;
  storage: string;
  selectedCloudName: string;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  setShowFinalDeployMessage: (show: boolean) => void;
  portMappings: any;
  pythonVersion: string;
  setPythonVersion: (version: string) => void;
  cudaVersion: string;
  setCudaVersion: (version: string) => void;
  lifecycleScriptTab: "upload" | "paste";
  setLifecycleScriptTab: (tab: "upload" | "paste") => void;
  lifecycleScriptFile: File | null;
  setLifecycleScriptFile: (file: File | null) => void;
  setShowConnectCloudsOrCardModal: (show: boolean) => void;
  noCardOnFile: boolean;
  availableLocations: Location[];
  selectedLocation: Location | null;
  setSelectedLocation: (location: Location | null) => void;
  instanceCount: number;
  setInstanceCount: (count: number) => void;
  hideContainerConfig?: boolean;
  hideRegionSelector?: boolean;
  launchableConfig?: LaunchableConfig;
  networkId?: string;
}

const DeploymentSection: React.FC<DeploymentSectionProps> = ({
  selectedInstanceType,
  container,
  setContainer,
  environmentName,
  handleNameChange,
  nameFieldErrorMessage,
  storage,
  selectedCloudName,
  loading,
  setLoading,
  setShowFinalDeployMessage,
  portMappings,
  pythonVersion,
  setPythonVersion,
  cudaVersion,
  setCudaVersion,
  lifecycleScriptTab,
  setLifecycleScriptTab,
  lifecycleScriptFile,
  setLifecycleScriptFile,
  setShowConnectCloudsOrCardModal,
  noCardOnFile,
  availableLocations,
  selectedLocation,
  setSelectedLocation,
  instanceCount,
  setInstanceCount,
  hideContainerConfig = false,
  hideRegionSelector = false,
  launchableConfig,
  networkId,
}) => {
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const billingContext = useContext(BillingContext);
  const workspaceContext = useContext(WorkspaceContext);
  const history = useHistory();
  const canViewBilling = isOrgAdminRole(orgContext.activeOrgRole);

  const { isEntitled } = useFreeTier();
  const { withinFreeTier: isStorageWithinFreeTier } = getStoragePricing(
    selectedInstanceType,
    Number(storage),
  );
  const isComputeFree =
    isEntitled &&
    isFreeTierInstance(
      getBasePriceAmount(selectedInstanceType, selectedLocation?.name),
    );
  const isStorageFree = isEntitled && isStorageWithinFreeTier;
  const isAllFreeTier = isComputeFree && isStorageFree;

  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [inlineAlertMessage, setInlineAlertMessage] = useState("");
  const [inlineAlertAction, setInlineAlertAction] = useState<any>(<></>);
  const [inlineAlertSeverity, setInlineAlertSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("error");
  const [instanceCountInput, setInstanceCountInput] = useState<string>(
    String(instanceCount),
  );
  const [showAdvancedSettings, setShowAdvancedSettings] =
    useState<boolean>(false);
  const [useCustomRegion, setUseCustomRegion] = useState<boolean>(false);
  const [customRegionValue, setCustomRegionValue] = useState<string>("");
  const [initialPulse, setInitialPulse] = useState<boolean>(true);

  useEffect(() => {
    const timer = setTimeout(() => setInitialPulse(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const renderContainerDisplay = () => {
    if (container?.vmBuild?.k8s && !container.vmBuild.k8s.isDisabled) {
      if (container.vmBuild.k8s.isDashboardEnabled) {
        return <p>Single-node Kubernetes w/ Dashboard</p>;
      }
      return <p>Single-node Kubernetes</p>;
    } else if (container?.vmBuild) {
      if (container?.vmBuild?.forceJupyterInstall) {
        return <p>VM Mode w/ Jupyter</p>;
      }
      return <p>VM Mode</p>;
    } else if (container?.verbBuild?.containerUrl) {
      if (container.verbBuild.containerUrl === "Default") {
        return (
          <p>
            Brev Container: (Python {pythonVersion} - CUDA {cudaVersion})
          </p>
        );
      }
      return <p>{displayContainerName(container.verbBuild.containerUrl)}</p>;
    } else if (container?.dockerCompose) {
      if (container.dockerCompose.jupyterInstall) {
        return <p>Docker Compose w/ Jupyter on host</p>;
      }
      return <p>Docker Compose</p>;
    } else if (container?.customContainer?.containerUrl) {
      return (
        <p>{displayContainerName(container.customContainer.containerUrl)}</p>
      );
    }
  };

  const isDeployDisabled = useMemo(() => {
    return (
      !billingContext.billingProfile?.billing_profile_id ||
      nameFieldErrorMessage !== "" ||
      environmentName === "" ||
      Number(storage) < 100 ||
      Number(storage) > 16000
    );
  }, [
    billingContext.billingProfile,
    nameFieldErrorMessage,
    environmentName,
    storage,
  ]);

  const providerDisplayName = useMemo(() => {
    const providerName = GetProviderName(selectedInstanceType || undefined);
    return providerName
      ? displayPlatformType(providerName)
      : "the cloud provider";
  }, [selectedInstanceType]);
  const selectedGpuName = selectedInstanceType?.supported_gpus?.[0]?.name || "";
  const selectedInstanceHasGpu = !!selectedInstanceType?.supported_gpus?.length;
  const isLaunchpad = isLaunchpadInstance(selectedInstanceType);

  const handleCompletion = async () => {
    const billingType = billingContext.billingProfile?.billing_type;
    const isBrevCloud = selectedCloudName === allPublicCloudsName;
    const shouldShowBillingModal = shouldShowCreateInstanceBillingModal({
      billingType,
      currentBalance: billingContext.currentBalance,
      noCardOnFile,
      isBrevCloud,
      isComputeFree,
      isStorageFree,
    });

    if (shouldShowBillingModal) {
      setShowConnectCloudsOrCardModal(true);
      return;
    }

    if (!selectedInstanceType) {
      setInlineAlertMessage("Please select an instance");
      setInlineAlertSeverity("info");
      return;
    }

    const cloudCreds = getInstanceCloudCreds(selectedInstanceType);

    if (cloudCreds.length === 0) {
      setInlineAlertMessage(
        "Please select a configuration from the options above.",
      );
      setInlineAlertSeverity("error");
      console.error("No cloud credentials available for this instance type");
      return;
    }

    let instanceCloudCred;
    if (
      !!(instanceCloudCred = cloudCreds.find(
        (cloudCred) => cloudCred.id === "dgxc-gcp-1",
      ))
    ) {
      instanceCloudCred = cloudCreds.find(
        (cloudCred) => cloudCred.id === "dgxc-gcp-1",
      );
    } else if (selectedCloudName === allPublicCloudsName) {
      instanceCloudCred = cloudCreds.find(
        (cloudCred) => cloudCred.tenantType === "shared",
      );
    } else {
      instanceCloudCred = cloudCreds.find(
        (cloudCred) =>
          cloudCred.id === selectedCloudName ||
          cloudCred.name === selectedCloudName,
      );
    }

    if (
      !instanceCloudCred ||
      !instanceCloudCred.locations ||
      instanceCloudCred.locations.length === 0
    ) {
      console.error("No locations available for this cloud credential");
      return;
    }

    // Use the user-selected location if specified, otherwise pass no location ("Any")
    // When selectedLocation is null, it means "Any" was selected, so we don't pass a region
    const region = selectedLocation;

    setLoading(true);
    setShowFinalDeployMessage(true);

    const customYaml = buildDefaultVerbYamlConfig(
      hasVerbBaseImageContainer(container)
        ? container?.verbBuild?.containerUrl
        : "",
      portMappings,
      cudaVersion,
      pythonVersion,
    );

    console.log("-------container:----------", container);

    const createSingleInstance = async (name: string) => {
      return workspaceContext.createWorkspace(
        name,
        orgContext.activeOrg?.id || "",
        instanceCloudCred.id,
        "",
        {},
        {},
        undefined,
        selectedInstanceType?.type,
        "",
        region?.name,
        "",
        storage || "120",
        false,
        false,
        false,
        container?.verbBuild?.containerUrl ? customYaml : "",
        hasVerbBaseImageContainer(container)
          ? container?.verbBuild?.containerUrl
          : "",
        container?.customContainer
          ? {
              containerUrl: container?.customContainer?.containerUrl,
              entryPoint: container?.customContainer?.entryPoint,
              registry: container?.customContainer?.registry,
            }
          : undefined,
        container?.vmBuild,
        portMappings,
        null,
        null,
        {
          isPublic: false,
        },
        "v1",
        container?.dockerCompose,
        launchableConfig,
        undefined,
        undefined,
        undefined,
        networkId,
      );
    };

    const results: Array<{
      name: string;
      success: boolean;
      id?: string;
      message?: string;
    }> = [];
    let hasAnySuccess = false;
    let lastSuccessId = "";

    for (let i = 0; i < instanceCount; i++) {
      const instanceName =
        instanceCount === 1 ? environmentName : `${environmentName}-${i + 1}`;
      const res = await createSingleInstance(instanceName);

      posthog.capture("Creating Instance", {
        instanceType: selectedInstanceType?.type,
        gpuName: selectedGpuName,
        cloudProvider: instanceCloudCred.platformType,
        cloud: selectedInstanceType?.cloud || selectedInstanceType?.provider,
        container: container?.vmBuild
          ? "VM Mode"
          : container?.verbBuild?.containerUrl ||
            container?.customContainer?.containerUrl ||
            container?.dockerCompose?.fileUrl,
        buildMode: getBuildModeLabel(container),
        orgID: orgContext.activeOrgId,
        userID: userContext.me?.id,
        wasSuccess: res.success,
        bulkDeployIndex: i + 1,
        bulkDeployTotal: instanceCount,
      });

      results.push({
        name: instanceName,
        success: res.success,
        id: res.data?.id,
        message: res.message,
      });

      if (res.success) {
        hasAnySuccess = true;
        lastSuccessId = res.data?.id || "";
      }
    }

    const successCount = results.filter((r) => r.success).length;
    const failedResults = results.filter((r) => !r.success);

    if (hasAnySuccess) {
      if (instanceCount === 1) {
        toast.info("Instance is starting");
      } else if (successCount === instanceCount) {
        toast.info(`All ${instanceCount} instances are starting`);
      } else {
        toast.info(
          `${successCount} of ${instanceCount} instances are starting`,
        );
      }

      if (instanceCount === 1) {
        history.push(
          `/org/${orgContext.activeOrgId}/environments/${lastSuccessId}`,
        );
      } else {
        history.push(`/org/${orgContext.activeOrgId}/environments`);
      }

      setLoading(false);
      setShowFinalDeployMessage(false);
    }

    if (failedResults.length > 0) {
      const firstError = failedResults[0];

      if (firstError.message?.includes("duplicate workspace with name")) {
        const duplicateNames = failedResults
          .filter((r) => r.message?.includes("duplicate workspace with name"))
          .map((r) => r.name);
        setInlineAlertMessage(
          duplicateNames.length > 1
            ? `Instances named "${duplicateNames.join('", "')}" already exist. Choose a different name.`
            : `An instance named "${duplicateNames[0]}" already exists. Choose a different name.`,
        );
        setInlineAlertSeverity("error");
        setInlineAlertAction(<></>);
      } else {
        console.error("Error in create instance: ", firstError.message);

        if (
          firstError.message?.includes(
            "user not allowed to create workspace of type",
          )
        ) {
          setInlineAlertMessage(
            canViewBilling
              ? "You need to add a payment method to access more powerful machines. You can do that from your org settings."
              : "Reach out to your organization admin to add a payment method and continue.",
          );
          setInlineAlertSeverity("error");
          setInlineAlertAction(
            canViewBilling ? (
              <Button
                label="Add payment method"
                type="secondary"
                hrefInNewTab={`/org/${orgContext.activeOrgId}/billing`}
                onClick={() => {
                  history.push(`/org/${orgContext.activeOrgId}/billing`);
                }}
              />
            ) : (
              <></>
            ),
          );
        } else if (
          firstError.message?.includes("have requested more vCPU capacity")
        ) {
          setInlineAlertMessage(
            "You have a limit on your AWS account preventing you from creating envs with this many vCPUs. Either create an env with the Brev Cloud or contact AWS support to increase your limit: https://aws.amazon.com/contact-us/ec2-request",
          );
          setInlineAlertSeverity("warning");
          setInlineAlertAction(<></>);
        } else if (firstError.message === "") {
          setInlineAlertMessage(
            "The provider has taken longer to respond than expected, the instance has likely been created. Refresh the instances page to check, if not try creating in a different region",
          );
          setInlineAlertSeverity("warning");
          setInlineAlertAction(<></>);
        } else if (
          firstError.message?.includes(
            "There was a billing issue because there is no payment method visit the settings page to resolve this issue",
          )
        ) {
          setInlineAlertMessage(
            canViewBilling
              ? "There was a billing issue because there is no payment method visit the settings page to resolve this issue"
              : "Reach out to your organization admin to add a payment method and continue.",
          );
          setInlineAlertSeverity("warning");
          setInlineAlertAction(
            canViewBilling ? (
              <Button
                label="Add payment method"
                type="secondary"
                hrefInNewTab={`/org/${orgContext.activeOrgId}/billing`}
                onClick={() => {
                  history.push(`/org/${orgContext.activeOrgId}/billing`);
                }}
              />
            ) : (
              <></>
            ),
          );
        } else {
          const errorMsg =
            instanceCount > 1
              ? `${failedResults.length} of ${instanceCount} instances failed: ${firstError.message}`
              : `Error creating instance: ${firstError.message}`;
          setInlineAlertMessage(errorMsg);
          setInlineAlertSeverity("error");
          setInlineAlertAction(<></>);
        }
      }

      if (!hasAnySuccess) {
        setLoading(false);
        setShowFinalDeployMessage(false);
        return null;
      }
    }

    setLoading(false);
  };

  return (
    <div className="mt-5">
      <NameEnvironment
        nameFieldErrorMessage={nameFieldErrorMessage}
        environmentName={environmentName}
        handleNameChange={handleNameChange}
      />
      {/* Advanced Settings Section */}
      <div className="mt-4">
        <div
          className="flex flex-row cursor-pointer text-highlight items-center"
          onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
        >
          <span className="text-sm text-highlight font-semibold hover:text-highlightDarker mr-1">
            Advanced
          </span>
          {showAdvancedSettings ? (
            <ChevronDownIcon className="w-4 h-4" />
          ) : (
            <ChevronRightIcon className="w-4 h-4" />
          )}
        </div>

        {showAdvancedSettings && (
          <div className="mt-3 pl-4 border-l-2 border-zinc-700">
            {/* Region/Location Selector */}
            {!hideRegionSelector && (
              <div className="flex flex-col mb-4">
                <div className="flex flex-row items-center mb-1">
                  <label className="block text-sm font-medium text-secondary">
                    <div className="flex flex-row items-center">
                      <MapPinIcon className="w-4 h-4 mr-1" />
                      Region
                      <InfoTooltip text="Regions vary across providers, so the available region values may differ depending on the selected instance type." />
                    </div>
                  </label>
                  <div className="ml-4 flex items-center gap-2">
                    <Checkbox
                      checked={useCustomRegion}
                      onCheckedChange={(checked) => {
                        const isChecked = checked === true;
                        setUseCustomRegion(isChecked);
                        if (!isChecked) {
                          setCustomRegionValue("");
                          setSelectedLocation(null);
                        }
                      }}
                    />
                    <label className="text-sm text-secondary cursor-pointer">
                      Custom
                    </label>
                  </div>
                </div>

                {useCustomRegion ? (
                  <Input
                    type="text"
                    placeholder="Enter custom region (e.g. us-east-1)"
                    value={customRegionValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCustomRegionValue(value);
                      if (value.trim()) {
                        setSelectedLocation({
                          name: value.trim(),
                          description: value.trim(),
                        });
                      } else {
                        setSelectedLocation(null);
                      }
                    }}
                    className="w-full max-w-xs"
                  />
                ) : (
                  <Select
                    value={selectedLocation?.name || "any"}
                    onValueChange={(value) => {
                      if (value === "any") {
                        setSelectedLocation(null);
                      } else {
                        const location = availableLocations.find(
                          (l) => l.name === value,
                        );
                        setSelectedLocation(location || null);
                      }
                    }}
                  >
                    <SelectTrigger className="w-full max-w-xs">
                      <SelectValue placeholder="Select a region">
                        {selectedLocation
                          ? regionToReadable[selectedLocation.name]
                            ? `${regionToReadable[selectedLocation.name].city}, ${regionToReadable[selectedLocation.name].country} (${selectedLocation.name})`
                            : selectedLocation.description ||
                              selectedLocation.name
                          : "Any"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="any" value="any">
                        Any
                      </SelectItem>
                      {availableLocations.map((location) => {
                        const info = regionToReadable[location.name];
                        return (
                          <SelectItem key={location.name} value={location.name}>
                            {info
                              ? `${info.city}, ${info.country} (${location.name})`
                              : location.description || location.name}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {/* Bulk Deploy - Number of Instances */}
            <div className="flex flex-col mb-4">
              <label className="block text-sm font-medium text-secondary mb-1">
                Bulk Deploy (Number of Instances)
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={instanceCountInput}
                onChange={(e) => {
                  const raw = e.target.value.trim();
                  setInstanceCountInput(raw);

                  if (raw === "") {
                    return;
                  }

                  const n = parseInt(raw, 10);
                  if (!isNaN(n) && n >= 1 && n <= 100) {
                    setInstanceCount(n);
                  }
                }}
                onBlur={() => {
                  if (instanceCountInput === "") {
                    const fallback = instanceCount || 1;
                    setInstanceCount(fallback);
                    setInstanceCountInput(String(fallback));
                    return;
                  }

                  let n = parseInt(instanceCountInput, 10);
                  if (isNaN(n) || n < 1) n = 1;
                  if (n > 100) n = 100;

                  setInstanceCount(n);
                  setInstanceCountInput(String(n));
                }}
                className="w-full max-w-xs px-3 py-2 border border-gray-300 dark:border-zinc-700 rounded-md bg-white dark:bg-zinc-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-highlight"
              />
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col mt-5">
        <div className="flex flex-row gap-4 items-center">
          <Button
            className={classNames(
              "",
              !isDeployDisabled || initialPulse ? "pulse-ready-button" : "",
            )}
            type={"primary"}
            label={
              instanceCount > 1 ? `Deploy ${instanceCount} Instances` : "Deploy"
            }
            disabled={isDeployDisabled}
            loading={loading}
            onClick={handleCompletion}
            data-testid="deploy-button"
          />
          <p className="text-xs text-secondary text-center mt-3">
            By clicking &apos;Deploy&apos;, I consent to sharing my personal and
            deployment data with {providerDisplayName} as described{" "}
            <a
              href="https://www.nvidia.com/en-us/agreements/cloud-services/service-specific-terms-for-brev/"
              target="_blank"
              rel="noreferrer"
              className="underline text-highlight"
            >
              here
            </a>
            .
          </p>
          {!hideContainerConfig && (
            <span className="flex flex-col justify-center">
              <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                <DrawerTrigger asChild>
                  <ButtonPrimitives variant="noshow">
                    <Tooltip
                      title="Configure a Container or Runtime"
                      placement="top"
                    >
                      <div className="flex flex-col">
                        <div className="flex flex-row h-full items-center text-white hover:cursor-pointer hover:text-highlight group">
                          <div className="flex flex-row items-center border border-white border-dashed rounded-md p-2 mr-2 group-hover:border-highlight">
                            <p className="text-xs">
                              {renderContainerDisplay()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Tooltip>
                  </ButtonPrimitives>
                </DrawerTrigger>
                <DrawerContent className="sm:max-w-3xl w-full">
                  <DrawerHeader>
                    <DrawerTitle>
                      <div className="flex flex-row items-center text-lg font-bold">
                        <CubeIcon className="w-6 h-6 mr-1" />
                        Configure your GPU environment
                      </div>
                    </DrawerTitle>
                  </DrawerHeader>
                  <DrawerBody>
                    <div className="flex flex-col">
                      <DrawerContainerSelector
                        closeDrawer={closeDrawer}
                        selectedContainer={container}
                        setSelectedContainer={setContainer}
                        pythonVersion={pythonVersion}
                        setPythonVersion={setPythonVersion}
                        cudaVersion={cudaVersion}
                        setCudaVersion={setCudaVersion}
                        lifecycleScriptTab={lifecycleScriptTab}
                        setLifecycleScriptTab={setLifecycleScriptTab}
                        lifecycleScriptFile={lifecycleScriptFile}
                        setLifecycleScriptFile={setLifecycleScriptFile}
                        selectedGpuName={selectedGpuName}
                        showRapidsOption={selectedInstanceHasGpu}
                        disableKubernetes={isLaunchpad}
                        kubernetesDisabledReason="Kubernetes is preconfigured for Launchpad instances. No additional cluster setup is required."
                      />
                    </div>
                  </DrawerBody>
                </DrawerContent>
              </Drawer>
            </span>
          )}
        </div>
        <InlineNotification
          show={!!inlineAlertMessage}
          severity={inlineAlertSeverity}
          className="w-full mt-5 pb-2"
          text={inlineAlertMessage}
          text2={<div className="mt-2 ml-[36px]">{inlineAlertAction}</div>}
          autoClose={false}
          onClose={() => {
            setInlineAlertMessage("");
            setInlineAlertAction(<></>);
            setInlineAlertSeverity("error");
          }}
        />
      </div>
    </div>
  );
};

export default DeploymentSection;
